import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Popconfirm,
  Space,
  Card,
  Row,
  Col,
  Tag,
  Input as AntInput,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { employeeService, Employee, CreateEmployeeRequest, UpdateEmployeeRequest } from '../services/employeeService';
import { useI18n } from '../hooks/useI18n';

const { Option } = Select;
const { Search } = AntInput;

const EmployeeManagementPage: React.FC = () => {
  const { t } = useI18n();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [searchType, setSearchType] = useState<'name' | 'employeeId'>('name');

  const currentTenant = localStorage.getItem('currentTenant') || 'default';

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const response = await employeeService.getEmployees(currentTenant);
      if (response.success) {
        setEmployees(response.data || []);
      } else {
        message.error(t('employeeManagement.loadFailed'));
      }
    } catch (error: any) {
      message.error(t('employeeManagement.loadFailed') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingEmployee(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (employee: Employee) => {
    setEditingEmployee(employee);
    form.setFieldsValue({
      employee_id: employee.employee_id,
      employee_name: employee.employee_name,
      phone_number: employee.phone_number,
      email: employee.email,
      department: employee.department,
      position: employee.position,
      is_active: Boolean(employee.is_active),
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await employeeService.deleteEmployee(currentTenant, id);
      if (response.success) {
        message.success(t('employeeManagement.deleteSuccess'));
        loadEmployees();
      } else {
        message.error(t('employeeManagement.deleteFailed'));
      }
    } catch (error: any) {
      message.error(t('employeeManagement.deleteFailed') + ': ' + error.message);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const requestData = {
        ...values,
        is_active: values.is_active ? 1 : 0,
      };

      if (editingEmployee) {
        // 更新员工
        const response = await employeeService.updateEmployee(
          currentTenant,
          editingEmployee.id,
          requestData
        );
        if (response.success) {
          message.success(t('employeeManagement.updateSuccess'));
          setModalVisible(false);
          loadEmployees();
        } else {
          message.error(t('employeeManagement.updateFailed'));
        }
      } else {
        // 创建员工
        const response = await employeeService.createEmployee(currentTenant, requestData);
        if (response.success) {
          message.success(t('employeeManagement.createSuccess'));
          setModalVisible(false);
          loadEmployees();
        } else {
          message.error(t('employeeManagement.createFailed'));
        }
      }
    } catch (error: any) {
      message.error((editingEmployee ? t('employeeManagement.updateFailed') : t('employeeManagement.createFailed')) + ': ' + error.message);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value.trim()) {
      loadEmployees();
      return;
    }

    try {
      setLoading(true);
      let response;
      if (searchType === 'name') {
        response = await employeeService.searchEmployeesByName(currentTenant, value);
      } else {
        response = await employeeService.searchEmployeesByEmployeeId(currentTenant, value);
      }

      if (response.success) {
        setEmployees(response.data || []);
      } else {
        message.error(t('employeeManagement.searchFailed'));
      }
    } catch (error: any) {
      message.error(t('employeeManagement.searchFailed') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: t('employeeManagement.employeeId'),
      dataIndex: 'employee_id',
      key: 'employee_id',
      width: 150,
    },
    {
      title: t('employeeManagement.employeeName'),
      dataIndex: 'employee_name',
      key: 'employee_name',
      width: 120,
      render: (text: string) => (
        <Space>
          <UserOutlined />
          {text}
        </Space>
      ),
    },
    {
      title: t('employeeManagement.phoneNumber'),
      dataIndex: 'phone_number',
      key: 'phone_number',
      width: 150,
      render: (text: string) => (
        text ? (
          <Space>
            <PhoneOutlined />
            {text}
          </Space>
        ) : '-'
      ),
    },
    {
      title: t('employeeManagement.email'),
      dataIndex: 'email',
      key: 'email',
      width: 200,
      render: (text: string) => (
        text ? (
          <Space>
            <MailOutlined />
            {text}
          </Space>
        ) : '-'
      ),
    },
    {
      title: t('employeeManagement.department'),
      dataIndex: 'department',
      key: 'department',
      width: 120,
      render: (text: string) => (
        text ? (
          <Space>
            <TeamOutlined />
            {text}
          </Space>
        ) : '-'
      ),
    },
    {
      title: t('employeeManagement.position'),
      dataIndex: 'position',
      key: 'position',
      width: 120,
    },
    {
      title: t('employeeManagement.status'),
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (isActive: number) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? t('employeeManagement.enabled') : t('employeeManagement.disabled')}
        </Tag>
      ),
    },
    {
      title: t('employeeManagement.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text: string) => new Date(text).toLocaleString(),
    },
    {
      title: t('employeeManagement.actions'),
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: Employee) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            {t('employeeManagement.edit')}
          </Button>
          <Popconfirm
            title={t('employeeManagement.confirmDelete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.ok')}
            cancelText={t('common.cancel')}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              {t('employeeManagement.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <h2 style={{ margin: 0 }}>
              <UserOutlined style={{ marginRight: 8 }} />
              {t('employeeManagement.title')}
            </h2>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              {t('employeeManagement.addEmployee')}
            </Button>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Select
              value={searchType}
              onChange={setSearchType}
              style={{ width: '100%' }}
            >
              <Option value="name">{t('employeeManagement.searchByName')}</Option>
              <Option value="employeeId">{t('employeeManagement.searchByEmployeeId')}</Option>
            </Select>
          </Col>
          <Col span={16}>
            <Search
              placeholder={t('employeeManagement.searchPlaceholder', { 
                type: searchType === 'name' ? t('employeeManagement.searchPlaceholderName') : t('employeeManagement.searchPlaceholderEmployeeId')
              })}
              enterButton={<SearchOutlined />}
              size="middle"
              onSearch={handleSearch}
              onChange={(e) => setSearchText(e.target.value)}
              value={searchText}
            />
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={employees}
          rowKey="id"
          loading={loading}
          pagination={{
            total: employees.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => t('employeeManagement.totalRecords', { total }),
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        title={editingEmployee ? t('employeeManagement.editEmployee') : t('employeeManagement.addEmployee')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="employee_id"
                label={t('employeeManagement.employeeId')}
                rules={[{ required: true, message: t('employeeManagement.employeeIdRequired') }]}
              >
                <Input placeholder={t('employeeManagement.employeeIdRequired')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="employee_name"
                label={t('employeeManagement.employeeName')}
                rules={[{ required: true, message: t('employeeManagement.employeeNameRequired') }]}
              >
                <Input placeholder={t('employeeManagement.employeeNameRequired')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="phone_number"
                label={t('employeeManagement.phoneNumber')}
              >
                <Input placeholder={t('employeeManagement.phoneNumber')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label={t('employeeManagement.email')}
                rules={[
                  { type: 'email', message: t('employeeManagement.emailInvalid') }
                ]}
              >
                <Input placeholder={t('employeeManagement.email')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="department"
                label={t('employeeManagement.department')}
              >
                <Input placeholder={t('employeeManagement.department')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="position"
                label={t('employeeManagement.position')}
              >
                <Input placeholder={t('employeeManagement.position')} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="is_active"
            label={t('employeeManagement.status')}
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren={t('employeeManagement.enabled')} unCheckedChildren={t('employeeManagement.disabled')} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="primary" htmlType="submit">
                {editingEmployee ? t('common.update') : t('common.create')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EmployeeManagementPage;
